import { Box, Text } from '@bonusly/design-system';

import type { RecognitionDraftCard } from '../../../schemas/bizy-ui-blocks';
import { AmountPill, HashtagToken, RecipientHandle } from './lib/recognition-segments';
import { UiBlockActionBar } from './ui-block-action-bar';

/**
 * Recognition draft card — the user is writing a recognition and
 * Bizy is offering a drafted message.
 *
 * Visual shape (matches the live feed's recognition rendering — see
 * `src/components/bonus/text-preview.tsx` for the source-of-truth
 * styling we're re-using):
 *
 *   Frederick Allen: [+10] @judith.bartels Thank you for ... #hashtag
 *
 *   [📋]                                   [Send now] [Edit in givebox]
 *
 * Each segment uses the same color treatment a real bonus uses, so the
 * draft reads visually like the post the user is about to commit. No
 * divider line — vertical spacing separates the body from the action
 * row (the action bar handles the `mt`).
 */
export function RecognitionDraftCardView({ block, fontSize = 'sm', isReadOnly = false }: { block: RecognitionDraftCard; fontSize?: 'sm' | 'md'; isReadOnly?: boolean }) {
	const { giver, recipient, amount, message, hashtags, actions } = block.payload;
	const recipients = Array.isArray(recipient) ? recipient : recipient ? [recipient] : [];
	const hashtagList = (hashtags ?? []).map((h) => h.replace(/^#/, ''));

	// Copy text reproduces what the user would paste into the give-box
	// input: amount + recipient mentions + body + hashtags. We hand
	// this to the action bar's left-side copy icon.
	const copyText = [
		amount && amount > 0 ? `+${amount}` : null,
		...recipients.map((r) => (r.startsWith('@') ? r : `@${r}`)),
		message,
		...hashtagList.map((h) => `#${h}`),
	]
		.filter(Boolean)
		.join(' ');

	return (
		<Box my={2} px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
			{/* `fontSize="inherit"` on every segment + Text keeps the giver header, +amount pill, @recipient, body, and hashtags at the same visual size. */}
			<Text fontSize={fontSize} lineHeight="tall" whiteSpace="pre-wrap">
				{giver && (
					<Text as="span" fontSize="inherit" fontWeight="semibold">
						{giver}:{' '}
					</Text>
				)}
				{amount != null && amount > 0 && (
					<>
						<AmountPill amount={amount} />{' '}
					</>
				)}
				{recipients.map((r, i) => (
					<span key={`${r}-${i}`}>
						<RecipientHandle username={r} />{' '}
					</span>
				))}
				{message}
				{hashtagList.length > 0 && (
					<>
						{' '}
						{hashtagList.map((h, i) => (
							<span key={`${h}-${i}`}>
								<HashtagToken hashtag={h} />
								{i < hashtagList.length - 1 ? ' ' : ''}
							</span>
						))}
					</>
				)}
			</Text>
			<UiBlockActionBar actions={actions} copyText={copyText} areActionsDisabled={isReadOnly} />
		</Box>
	);
}
